package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import kotlin.jvm.JvmStatic
import kotlin.math.ceil

/**
 * 二维等大格子虚拟网格：仅实例化视窗 + buffer 范围内的格子，inventory / 物品栏类场景必备。
 *
 * 填充顺序由 [flow] 决定：
 * - [Flow.ROW_MAJOR]（默认）：固定 [columns] 列，按行铺满，垂直滚动；行数 = ceil(itemCount / columns)。
 * - [Flow.COLUMN_MAJOR]：固定 [rows] 行，按列铺满，水平滚动；列数 = ceil(itemCount / rows)。
 *
 * 窗口子节点按 absolute 定位摆放（layout=ABSOLUTE），每个格子由 IrInterpreter 注入 left/top/width/height。
 * 内容尺寸按格子总跨度计算，故 [requiresContentMeasure] 返回 false。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class VirtualGrid private constructor() : ScrollableContainer<VirtualGrid>() {

    /** 填充顺序。 */
    enum class Flow {
        ROW_MAJOR,
        COLUMN_MAJOR,
    }

    private var itemCountField = 0
    private var itemWidthField = 24f
    private var itemHeightField = 24f
    private var gapXField = 2f
    private var gapYField = 2f
    private var flowField = Flow.ROW_MAJOR
    private var columnsField = 0
    private var rowsField = 0
    private var bufferField = 1

    private var windowChildren: List<UIElement<*>> = emptyList()
    private var lastFirst = -1
    private var lastLast = -1
    private var lastItemCount = -1
    private var materializer: WindowMaterializer? = null

    init {
        // 子节点 absolute 定位，由 grid 自身按窗口范围给定坐标。
        styleField.layout().mode = LayoutMode.ABSOLUTE
    }

    override fun self(): VirtualGrid = this

    // -------- 属性 --------

    fun itemCount(count: Int): VirtualGrid = apply {
        itemCountField = count.coerceAtLeast(0)
        scrollY(scrollY())
        scrollX(scrollX())
        invalidateWindow()
    }

    fun itemCount(): Int = itemCountField

    fun itemWidth(w: Float): VirtualGrid = apply {
        itemWidthField = w.coerceAtLeast(1f)
        invalidateWindow()
    }

    fun itemWidth(): Float = itemWidthField

    fun itemHeight(h: Float): VirtualGrid = apply {
        itemHeightField = h.coerceAtLeast(1f)
        invalidateWindow()
    }

    fun itemHeight(): Float = itemHeightField

    fun gapX(g: Float): VirtualGrid = apply {
        gapXField = g.coerceAtLeast(0f)
        invalidateWindow()
    }

    fun gapX(): Float = gapXField

    fun gapY(g: Float): VirtualGrid = apply {
        gapYField = g.coerceAtLeast(0f)
        invalidateWindow()
    }

    fun gapY(): Float = gapYField

    fun flow(f: Flow?): VirtualGrid = apply {
        flowField = f ?: Flow.ROW_MAJOR
        // ROW_MAJOR 垂直滚，COLUMN_MAJOR 水平滚。
        super.direction(if (flowField == Flow.ROW_MAJOR) ScrollDirection.VERTICAL else ScrollDirection.HORIZONTAL)
        invalidateWindow()
    }

    fun flow(): Flow = flowField

    fun columns(c: Int): VirtualGrid = apply {
        columnsField = c.coerceAtLeast(0)
        invalidateWindow()
    }

    fun columns(): Int = columnsField

    fun rows(r: Int): VirtualGrid = apply {
        rowsField = r.coerceAtLeast(0)
        invalidateWindow()
    }

    fun rows(): Int = rowsField

    fun buffer(b: Int): VirtualGrid = apply {
        bufferField = b.coerceAtLeast(0)
        invalidateWindow()
    }

    fun buffer(): Int = bufferField

    // -------- 派生的行列数（0 表示按视窗自适应） --------

    /** 有效列数：ROW_MAJOR 用显式 columns（0 时按视窗宽度推算，至少 1）。 */
    fun effectiveColumns(): Int {
        if (flowField == Flow.ROW_MAJOR) {
            if (columnsField > 0) {
                return columnsField
            }
            return ((viewportWidth() + gapXField) / (itemWidthField + gapXField)).toInt().coerceAtLeast(1)
        }
        // COLUMN_MAJOR：列数 = ceil(itemCount / rows)
        val r = effectiveRows()
        return if (r <= 0) 0 else ceil(itemCountField.toDouble() / r).toInt()
    }

    /** 有效行数：COLUMN_MAJOR 用显式 rows（0 时按视窗高度推算，至少 1）。 */
    fun effectiveRows(): Int {
        if (flowField == Flow.COLUMN_MAJOR) {
            if (rowsField > 0) {
                return rowsField
            }
            return ((viewportHeight() + gapYField) / (itemHeightField + gapYField)).toInt().coerceAtLeast(1)
        }
        // ROW_MAJOR：行数 = ceil(itemCount / columns)
        val c = effectiveColumns()
        return if (c <= 0) 0 else ceil(itemCountField.toDouble() / c).toInt()
    }

    // -------- 内容尺寸 --------

    override fun requiresContentMeasure(): Boolean = false

    override fun contentWidth(): Float {
        val c = effectiveColumns()
        return if (c <= 0) 0f else c * itemWidthField + (c - 1) * gapXField
    }

    override fun contentHeight(): Float {
        val r = effectiveRows()
        return if (r <= 0) 0f else r * itemHeightField + (r - 1) * gapYField
    }

    // -------- 格子坐标 --------

    /** 第 index 个格子的左上角 X（相对内容原点）。 */
    fun cellX(index: Int): Float = column(index) * (itemWidthField + gapXField)

    /** 第 index 个格子的左上角 Y（相对内容原点）。 */
    fun cellY(index: Int): Float = row(index) * (itemHeightField + gapYField)

    /** 第 index 个格子所在列。 */
    fun column(index: Int): Int = if (flowField == Flow.ROW_MAJOR) {
        index % effectiveColumns().coerceAtLeast(1)
    } else {
        index / effectiveRows().coerceAtLeast(1)
    }

    /** 第 index 个格子所在行。 */
    fun row(index: Int): Int = if (flowField == Flow.ROW_MAJOR) {
        index / effectiveColumns().coerceAtLeast(1)
    } else {
        index % effectiveRows().coerceAtLeast(1)
    }

    // -------- 窗口范围 --------

    /** 第一个需实例化的格子 index（含 buffer，clamp 到 [0, itemCount-1]）。 */
    fun firstVisible(): Int {
        if (itemCountField <= 0) {
            return 0
        }
        return if (flowField == Flow.ROW_MAJOR) {
            val cols = effectiveColumns().coerceAtLeast(1)
            val firstRow = ((scrollY() / (itemHeightField + gapYField)).toInt() - bufferField).coerceAtLeast(0)
            (firstRow * cols).coerceAtMost(itemCountField - 1)
        } else {
            val rowsN = effectiveRows().coerceAtLeast(1)
            val firstCol = ((scrollX() / (itemWidthField + gapXField)).toInt() - bufferField).coerceAtLeast(0)
            (firstCol * rowsN).coerceAtMost(itemCountField - 1)
        }
    }

    /** 最后一个需实例化的格子 index（含 buffer，clamp 到 [0, itemCount-1]）。 */
    fun lastVisible(): Int {
        if (itemCountField <= 0) {
            return -1
        }
        return if (flowField == Flow.ROW_MAJOR) {
            val cols = effectiveColumns().coerceAtLeast(1)
            val firstRow = ((scrollY() / (itemHeightField + gapYField)).toInt() - bufferField).coerceAtLeast(0)
            val visibleRows = ceil(viewportHeight() / (itemHeightField + gapYField)).toInt() + bufferField * 2
            ((firstRow + visibleRows + 1) * cols - 1).coerceAtMost(itemCountField - 1)
        } else {
            val rowsN = effectiveRows().coerceAtLeast(1)
            val firstCol = ((scrollX() / (itemWidthField + gapXField)).toInt() - bufferField).coerceAtLeast(0)
            val visibleCols = ceil(viewportWidth() / (itemWidthField + gapXField)).toInt() + bufferField * 2
            ((firstCol + visibleCols + 1) * rowsN - 1).coerceAtMost(itemCountField - 1)
        }
    }

    // -------- 子节点管理 --------

    override fun children(): List<UIElement<*>> = windowChildren

    fun setWindowChildren(children: List<UIElement<*>>?) {
        windowChildren = children ?: emptyList()
        // FR-30 B1：windowChildren 经 children() 返回、绕过会标脏的 child()/replaceChildren()，
        // 滚动实例化新窗口时须显式标布局脏，否则整树布局 memo 命中返回过期布局、新窗口项不被定位（滚动后内容消失 / 堆顶）。
        markLayoutDirty()
    }

    fun materializeWindow(m: WindowMaterializer) {
        materializer = m
    }

    override fun onScrollStateChanged() {
        super.onScrollStateChanged()
        invalidateWindow()
    }

    private fun invalidateWindow() {
        val m = materializer ?: return
        if (itemCountField <= 0) {
            if (windowChildren.isNotEmpty()) {
                lastFirst = -1
                lastLast = -1
                lastItemCount = 0
                m.onWindowChanged(this, 0, -1)
            }
            return
        }
        val first = firstVisible()
        val last = lastVisible()
        if (first == lastFirst && last == lastLast && itemCountField == lastItemCount) {
            return
        }
        lastFirst = first
        lastLast = last
        lastItemCount = itemCountField
        m.onWindowChanged(this, first, last)
    }

    fun interface WindowMaterializer {
        /**
         * 窗口范围变化时调用。
         *
         * @param grid  VirtualGrid 实例
         * @param first 第一个需实例化的 index（含）
         * @param last  最后一个需实例化的 index（含）；itemCount=0 时 last < first
         */
        fun onWindowChanged(grid: VirtualGrid, first: Int, last: Int)
    }

    companion object {
        @JvmStatic
        fun create(): VirtualGrid = VirtualGrid()
    }
}
